import { get } from "lodash";
import { Formik, Form, ErrorMessage } from "formik";
import * as yup from "yup";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { PageRequestError } from "components/PageRequestError";
import { ProgressSpinner } from "primereact/progressspinner";
import { Title } from "components/Title";
import { Dropdown } from "primereact/dropdown";
import useApp from "hooks/useApp";
import useEditPage from "hooks/useEditPage";
import { ColorPicker } from "primereact/colorpicker";
import { useEffect, useState } from "react";
import { RadioButton } from "primereact/radiobutton";
import FontAwesomeIconPicker from "components/FontAwesomeIconPicker";
import { InputTextarea } from "primereact/inputtextarea";
import { useTranslation } from "react-i18next";
const ModulesEditPage = (props) => {
  const [selectedIcon, setSelectedIcon] = useState("");
  const [firstRadioButton, setFirstRadioButton] = useState(false);
  const [secondRadioButton, setSecondRadioButton] = useState(false);
  const [checked,setChecked]=useState("")
  const { t } = useTranslation();
  const app = useApp();
  // form validation schema
  const validationSchema = yup.object().shape({
    org_code: yup.string().required(t("FACILITY_REQUIRED")),
    code: yup.string().required(t("CODE_REQUIRED")),
    module_name: yup.string().required(t("NAME_REQUIRED")),
    color: yup.string().nullable().label("Color"),
    icon: yup.string().nullable().label("Icon"),
    module_order: yup.number().nullable().label("Module Order"),
    module_config: yup
      .string()
      .nullable()
      .test("is-json", t("INVALID_JSON_FORMAT"), (value) => {
        if (!value) return true; // Allow empty string
        try {
          JSON.parse(value);
          return true;
        } catch (e) {
          return false;
        }
      }),
  });
  // form default values
  const formDefaultValues = {
    org_code: "",
    code: "",
    module_name: "",
    module_config: "",
    color: "",
    icon: "",
    module_order: 0,
  };
  //where page logics resides
  const pageController = useEditPage({ props, formDefaultValues, afterSubmit });
  //destructure and grab what we need
  const {
    formData,
    handleSubmit,
    submitForm,
    pageReady,
    loading,
    saving,
    apiRequestError,
    inputClassName,
  } = pageController;

  useEffect(() => {
    if (!formData?.icon?.includes("/")) {
      setFirstRadioButton(true);
      setSecondRadioButton(false);
      setSelectedIcon(formData.icon);
    } else if (formData?.icon?.includes("/")) {
      setFirstRadioButton(false);
      setSecondRadioButton(true);
      setSelectedIcon(formData.icon);
    }
  }, [formData]);
  const getInputClassName = (error, touched) => {
    return error && touched ? "p-invalid" : "";
  };

  const handleRadioButton = (e) => {
    if (e.value === "iconInput") {
      setFirstRadioButton(true);
      setSecondRadioButton(false);
      setSelectedIcon("");
    } else {
      setFirstRadioButton(false);
      setSecondRadioButton(true);
      setSelectedIcon("");
    }
  };
  const handleReset = (formik) => {
    formik.resetForm({
      values: {
        ...formDefaultValues,
        org_code: formik.values.org_code,
        code: formik.values.code,
      },
    });
    setSelectedIcon("");
    setFirstRadioButton(false);
    setSecondRadioButton(false);
  };

  //Event raised on form submit success
  function afterSubmit(response) {
    app.flashMsg(props.msgTitle, props.msgAfterSave);
    app.getAllModules();
    if (app.isDialogOpen()) {
      app.closeDialogs(); // if page is open as dialog, close dialog
    } else if (props.redirect) {
      app.navigate(`/modules`);
    }
  }
  useEffect(() => {
    setSelectedIcon("");
  }, [checked]);
  // loading form data from api
  if (loading) {
    return (
      <div className="p-3 text-center">
        <ProgressSpinner style={{ width: "50px", height: "50px" }} />
      </div>
    );
  }
  //display error page
  if (apiRequestError) {
    return <PageRequestError error={apiRequestError} />;
  }

  //page is ready when formdata loaded successfully
  if (pageReady) {
    return (
      <main id="ModulesEditPage" className="main-page">
        {props.showHeader && (
          <section className="page-section mb-3">
            <div className="container">
              <div className="grid align-items-center">
                {!props.isSubPage && (
                  <div className="col-fixed ">
                    <Button
                      onClick={() => app.navigate(-1)}
                      label=""
                      className="p-button p-button-text "
                      icon="pi pi-arrow-left"
                    />
                  </div>
                )}
                <div className="col ">
                  <Title
                    title={t("EDIT_MODULE")}
                    titleClass="text-2xl text-primary font-bold"
                    subTitleClass="text-500"
                    separator={false}
                  />
                </div>
              </div>
            </div>
          </section>
        )}
        <section className="page-section ">
          <div className="container">
            <div className="grid ">
              <div className="md:col-9 sm:col-12 comp-grid">
                <div>
                  <Formik
                    initialValues={formData}
                    validationSchema={validationSchema}
                    onSubmit={(values, actions) => {
                      const newValues = { ...values, icon: selectedIcon };
                      submitForm(newValues);
                    }}
                  >
                    {(formik) => {
                      return (
                        <Form className={`${!props.isSubPage ? "card  " : ""}`}>
                          <div className="grid">
                            <div className="col-12">
                              <div className="formgrid grid">
                                <div className="col-12 md:col-3">Id *</div>
                                <div className="col-12 md:col-9">
                                  <InputText
                                    name="id"
                                    disabled
                                    onChange={formik.handleChange}
                                    value={formik.values.id}
                                    label="Id"
                                    type="number"
                                    placeholder="Enter Id"
                                    min={0}
                                    step="any"
                                    className={inputClassName(
                                      formik?.errors?.id
                                    )}
                                  />
                                  <ErrorMessage
                                    name="id"
                                    component="span"
                                    className="p-error"
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="formgrid grid">
                                <div className="col-12 md:col-3">
                                  {t("FACILITIES")} *
                                </div>
                                <div className="col-12 md:col-9">
                                  <Dropdown
                                    name="org_code"
                                    value={formik.values.org_code}
                                    onChange={formik.handleChange}
                                    options={get(props, "organisations", [])}
                                    optionLabel="name"
                                    optionValue="code"
                                    disabled
                                    placeholder={t("SELECT_FACILITY")}
                                    className={inputClassName(
                                      formik?.errors?.org_code
                                    )}
                                  />
                                  <ErrorMessage
                                    name="org_code"
                                    component="span"
                                    className="p-error"
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="formgrid grid">
                                <div className="col-12 md:col-3">
                                  {t("CODE")} *
                                </div>
                                <div className="col-12 md:col-9">
                                  <InputText
                                    name="code"
                                    disabled
                                    onChange={formik.handleChange}
                                    value={formik.values.code}
                                    label="Code"
                                    type="text"
                                    placeholder={t("ENTER_CODE")}
                                    className={inputClassName(
                                      formik?.errors?.code
                                    )}
                                  />
                                  <ErrorMessage
                                    name="code"
                                    component="span"
                                    className="p-error"
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="formgrid grid">
                                <div className="col-12 md:col-3">
                                  {t("NAME")} *
                                </div>
                                <div className="col-12 md:col-9">
                                  <InputText
                                    name="module_name"
                                    onChange={formik.handleChange}
                                    value={formik.values.module_name}
                                    label="Name"
                                    type="text"
                                    placeholder={t("ENTER_NAME")}
                                    className={`w-full ${getInputClassName(
                                      formik.errors.module_name,
                                      formik.touched.module_name
                                    )}`}
                                    {...(formik.values.module_name && {
                                      showClear: true,
                                    })}
                                  />
                                  <ErrorMessage
                                    name="module_name"
                                    component="span"
                                    className="p-error"
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="formgrid grid">
                                <div className="col-12 md:col-3">
                                  {t("CONFIGURATION")}
                                </div>
                                <div className="col-12 md:col-9">
                                  <InputTextarea
                                    name="module_config"
                                    onChange={formik.handleChange}
                                    value={formik.values.module_config}
                                    label="Configuration"
                                    // type="text"
                                    placeholder={t("ENTER_CONFIGURATION")}
                                    className={inputClassName(
                                      formik?.errors?.module_config
                                    )}
                                    rows={5}
                                    cols={30}
                                  />
                                  <ErrorMessage
                                    name="module_config"
                                    component="span"
                                    className="p-error"
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="formgrid grid">
                                <div className="col-12 md:col-3">
                                  {t("MODULE_ORDER")}
                                </div>
                                <div className="col-12 md:col-9">
                                  <InputText
                                    name="module_order"
                                    onChange={formik.handleChange}
                                    value={formik.values.module_order}
                                    label="module_order"
                                    type="number"
                                    placeholder={t("ENTER_MODULE_ORDER")}
                                    className={inputClassName(
                                      formik?.errors?.module_order
                                    )}
                                  />
                                  <ErrorMessage
                                    name="module_order"
                                    component="span"
                                    className="p-error"
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="formgrid grid">
                                <div className="col-12 md:col-3">
                                  {t("CHOOSE_COLOUR")}
                                </div>
                                <div className="col-12 md:col-9">
                                  <ColorPicker
                                    name="color"
                                    value={formik.values.color}
                                    onChange={formik.handleChange}
                                  />
                                  <ErrorMessage
                                    name="color"
                                    component="span"
                                    className="p-error"
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="formgrid grid">
                                <div className="col-12 md:col-3">
                                  {t("ICON")}
                                </div>
                                <div className="col-12 md:col-9">
                                  {/* <Dropdown name="icon" value={selectedIcon}onChange={(e) => setSelectedIcon(e.value)} options={Icons}  optionLabel="name" placeholder="Select a Icon" className="w-full md:w-14rem" />                                   */}
                                  <RadioButton
                                    inputId="icon1"
                                    name="icon1"
                                    value="iconInput"
                                    onChange={handleRadioButton}
                                    checked={firstRadioButton}
                                  />
                                  <label htmlFor="icon1" className="ml-2">
                                    {t("CHOOSE_ICON")}
                                  </label>{" "}
                                  <RadioButton
                                    inputId="icon2"
                                    name="icon2"
                                    value="iconLink"
                                    onChange={handleRadioButton}
                                    checked={secondRadioButton}
                                  />
                                  <label htmlFor="icon2" className="ml-2">
                                    {t("UPLOAD ICON LINK")}
                                  </label>
                                  <div style={{ marginTop: "1rem" }}>
                                    {secondRadioButton && (
                                      <InputText
                                        value={selectedIcon}
                                        onChange={(e) =>
                                          setSelectedIcon(e.target.value)
                                        }
                                        placeholder={t("ADD_ICON_LINK_HERE")}
                                        className={inputClassName(
                                          formik?.errors?.icon
                                        )}
                                      />
                                    )}
                                    {firstRadioButton && (
                                      <FontAwesomeIconPicker
                                        name="icon"
                                        value={selectedIcon}
                                        onChange={setSelectedIcon}
                                      />
                                    )}
                                  </div>
                                  <ErrorMessage
                                    name="icon"
                                    component="span"
                                    className="p-error"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                          {props.showFooter && (
                            <div className="text-center my-3">
                              <Button
                                onClick={(e) => handleSubmit(e, formik)}
                                type="submit"
                                label={t("UPDATE")}
                                icon="pi pi-send"
                                loading={saving}
                                className="mr-2"
                              />
                              <Button
                                type="button"
                                label={t("RESET")}
                                icon="pi pi-refresh"
                                className="p-button-secondary"
                                onClick={() => handleReset(formik)}
                              />
                            </div>
                          )}
                        </Form>
                      );
                    }}
                  </Formik>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    );
  }
};

const withDefaultProps = (Component) => {
  const WrappedComponent = (props) => {
    const { t } = useTranslation();
    const defaultProps = {
      primaryKey: "id",
      pageName: "modules",
      getApiPath: "modules/get_module",
      putApiPath: "modules/edit_module",
      routeName: "modulesedit",
      submitButtonLabel: "Update",
      formValidationError: t("FORM_IS_INVALID"),
      formValidationMsg: t("PLEASE_COMPLETE_THE_FORM"),
      msgTitle: t("UPDATE_MODULE"),
      msgAfterSave: t("MODULE_UPDATED_SUCCESSFULLY"),
      msgBeforeSave: "",
      showHeader: true,
      showFooter: true,
      redirect: true,
      isSubPage: false,
    };

    return <Component {...defaultProps} {...props} />;
  };

  return WrappedComponent;
};
export default withDefaultProps(ModulesEditPage);
